import { useEffect, useState } from "react";
import { api } from "./api";

// Ball Handling v5 — section rows (interactions, linked skills, stats, load × security grid, flags, outcomes, LFs by outcome, constraints, Base Dribble).
let LIB = null, P = null;
export const loadBallHandling = () => LIB ? Promise.resolve(LIB) : (P ||= api.ballHandlingLibrary().then(d => { LIB = d; return d; }).catch(e => { P = null; throw e; }));
export const useBallHandling = () => { const [lib, setLib] = useState(LIB); useEffect(() => { if (!lib) loadBallHandling().then(setLib).catch(() => {}); }, [lib]); return lib; };

export const bhList = (lib, list) => (lib?.items || []).filter(i => i.list === list && i.status === "active").sort((a, b) => a.order - b.order);
export const bhItem = (lib, id) => (lib?.items || []).find(i => i.id === id && i.status === "active");
export const lfOf = (lib, id) => (lib?.limiting_factors || []).find(f => f.id === id);
export const lfName = (lib, id) => lfOf(lib, id)?.name || id;
export const solOf = (lib, id) => (lib?.solutions || []).find(s => s.id === id);
export const HANDLE_TAGS = ["BO-01", "BO-02", "BO-03", "BO-04", "BO-05", "BO-06", "BO-07", "BO-08"];
export const PASS_TAGS = ["BO-09", "BO-11", "BO-12"];
export const SHARED_TAG = "FO-05";

const n = (v) => (v == null || v === "" || Number.isNaN(Number(v)) ? null : Number(v));
const div = (a, b) => (a == null || !b ? null : a / b);
const frac = (v) => (v == null ? null : v > 1 ? v / 100 : v);   // "12%" is stored as 0.12; a typed 12 means 12%
const tierOf = (v, cuts, labels) => (v == null ? null : labels[cuts.reduce((t, c) => (v >= c ? t + 1 : t), 0)]);
const T4 = ["Struggling", "Average", "Good", "Elite"];
export const tovTier = (v) => (v == null ? null : v < 0.10 ? "Elite" : v < 0.14 ? "Good" : v < 0.18 ? "Average" : "Struggling");
export const astToTier = (v) => tierOf(v, [1.0, 1.5, 2.5], T4);
export const loadTier = (v) => tierOf(v, [3, 6], ["Light", "Regular", "Heavy"]);
export const pppTier = (v) => tierOf(v, [0.80, 0.90, 1.00], T4);
export const toTier = (v) => (v == null ? null : v < 0.12 ? "Good" : v < 0.16 ? "Average" : "Struggling");

// Stats panel from a Season Stats record (SS- ids) + the Finishing 1A result (reused, never recalculated) → tiers, grid cell, flags (BHF-).
export function computeBallHandling(stats, fin) {
  if (!stats) return null;
  const s = (id) => n(stats[id]);
  const g = s("SS-001"), fga = s("SS-004"), fta = s("SS-008"), ast = s("SS-012"), tov = s("SS-015"), astPct = frac(s("SS-021"));
  const pnrPoss = s("SS-060"), pnrPpp = s("SS-061"), pnrTo = frac(s("SS-122")), isoPoss = s("SS-068"), isoPpp = s("SS-069"), isoTo = frac(s("SS-134")), trTo = frac(s("SS-143"));
  const hasSyn = [pnrPoss, isoPoss, pnrPpp, isoPpp].some(v => v != null);
  const tovPct = tov != null && fga != null ? div(tov, fga + 0.44 * (fta || 0) + tov) : null;
  const astTo = tov != null && ast != null ? (tov > 0 ? ast / tov : ast > 0 ? Infinity : null) : null;
  const apg = div(ast, g);
  const synLoad = hasSyn && (pnrPoss != null || isoPoss != null) && g ? ((pnrPoss || 0) + (isoPoss || 0)) / g : null;
  const boxLoad = ast != null && tov != null && g ? (ast + tov) / g : null;
  const bih = synLoad ?? boxLoad;
  const load = loadTier(bih), sec = tovTier(tovPct);
  const paint = fin?.a ? { perGame: fin.a.perGame, tier: fin.a.volumeTier || null, mode: fin.a.mode, label: fin.a.label } : null;
  const ftRate = div(fta, fga);
  const flags = [];
  if (tovPct != null && tovPct >= 0.18) flags.push("BHF-01");
  if (astTo != null && astTo < 1.0) flags.push("BHF-02");
  if (trTo != null && trTo >= 0.16) flags.push("BHF-03");
  if (isoPpp != null && isoPpp < 0.80 && g && (isoPoss || 0) / g >= 1) flags.push("BHF-04");
  if (load === "Heavy" && paint?.tier === "Rarely in the paint") flags.push("BHF-05");
  if (load === "Heavy" && astPct != null && astPct < 0.15) flags.push("BHF-06");
  return { games: g, ast, tov, apg, astPct, tovPct, tovTier: sec, astTo, astToTier: astToTier(astTo), bih, load, loadMode: synLoad != null ? "synergy" : "box",
    hasSyn, pnr: { poss: pnrPoss, perGame: div(pnrPoss, g), ppp: pnrPpp, pppTier: pppTier(pnrPpp), to: pnrTo, toTier: toTier(pnrTo) },
    iso: { poss: isoPoss, perGame: div(isoPoss, g), ppp: isoPpp, pppTier: pppTier(isoPpp), to: isoTo, toTier: toTier(isoTo) },
    paint, ftRate, flags };
}
export const gridCell = (lib, result) => result?.load && result?.tovTier ? bhList(lib, "Load x Security").find(c => c.load === result.load && c.security === result.tovTier) : null;
export const fmtPct = (v) => (v == null || Number.isNaN(v) ? "—" : `${(v * 100).toFixed(1)}%`);
export const fmt1 = (v) => (v == null || Number.isNaN(v) ? "—" : v === Infinity ? "∞" : Number(v).toFixed(1));
export const fmt2 = (v) => (v == null || Number.isNaN(v) ? "—" : Number(v).toFixed(2));
